import { useEffect, useRef, useState, useCallback } from 'react';
import { pdfjs } from 'react-pdf';
import { Button } from '@/components/ui/button';
import {
  X, ZoomIn, ZoomOut, Maximize2, Minimize2, RotateCcw, Loader2, ExternalLink,
} from 'lucide-react';
import { getFurnitureImageUrl } from '@/lib/api';

// Worker pinned to the same CDN build as pdfjs-dist bundled here — matches
// the setup in ReceiptProcessingPage so we don't ship two versions.
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DEFAULT_PANEL_WIDTH = 460;
const WIDE_PANEL_WIDTH = 760;
const ZOOM_STEP = 0.25;
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;

// Render every page of the PDF to a canvas and stitch them top-to-bottom
// into one tall PNG dataURL so the viewer can treat it like a regular image.
async function renderPdfToDataUrl(url) {
  const loadingTask = pdfjs.getDocument(url);
  const doc = await loadingTask.promise;
  const scale = 2; // 2x DPI is a good readability/size balance
  const pages = [];
  let totalHeight = 0;
  let maxWidth = 0;
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({
      canvasContext: canvas.getContext('2d'),
      viewport,
      canvas,  // pdfjs v5+ requires canvas in the options
    }).promise;
    pages.push({ canvas, w: canvas.width, h: canvas.height });
    totalHeight += canvas.height;
    if (canvas.width > maxWidth) maxWidth = canvas.width;
  }
  const stitched = document.createElement('canvas');
  stitched.width = maxWidth;
  stitched.height = totalHeight;
  const ctx = stitched.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, maxWidth, totalHeight);
  let y = 0;
  for (const p of pages) {
    ctx.drawImage(p.canvas, 0, y);
    y += p.h;
  }
  return stitched.toDataURL('image/png');
}

/**
 * Receipt viewer with zoom + pan.
 * - mode="panel": fixed right-side sliding panel with backdrop
 * - mode="embedded": inline viewer (no fixed positioning, no close button)
 */
export function ReceiptViewerPanel({
  open = true,
  onClose,
  receipt,
  mode = 'panel',
  extras = null,
  title,
}) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [wide, setWide] = useState(false);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragState = useRef(null); // { startX, startY, panX, panY }
  const viewportRef = useRef(null);
  const imgRef = useRef(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });

  const url = receipt ? getFurnitureImageUrl(receipt.file_path) : null;
  const isPdf = receipt?.content_type === 'application/pdf' ||
    (receipt?.file_name || '').toLowerCase().endsWith('.pdf');

  // Load / render source whenever the active receipt changes.
  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    setError(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    if (!receipt || !open) return undefined;
    if (!isPdf) { setSrc(url); return undefined; }
    setLoading(true);
    (async () => {
      try {
        const dataUrl = await renderPdfToDataUrl(url);
        if (!cancelled) setSrc(dataUrl);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to render PDF');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // We don't depend on the entire receipt object (it changes identity each
    // list refresh); keying off file_path + id avoids infinite reloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt?.id, receipt?.file_path, open, isPdf, url]);

  // Clamp pan so the image cannot be dragged past the edges. Anchor at
  // top-left: pan (0,0) = image's top-left against viewport's top-left.
  // Valid pan range is [-(effective - viewport), 0] on each axis. If the
  // image is SMALLER than the viewport along that axis, pan stays at 0.
  const clamp = useCallback((nx, ny) => {
    const vp = viewportRef.current;
    if (!vp || !natural.w) return { x: nx, y: ny };
    const effW = natural.w * zoom;
    const effH = natural.h * zoom;
    const minX = Math.min(0, vp.clientWidth - effW);
    const minY = Math.min(0, vp.clientHeight - effH);
    return {
      x: Math.max(minX, Math.min(0, nx)),
      y: Math.max(minY, Math.min(0, ny)),
    };
  }, [natural, zoom]);

  // Re-clamp whenever zoom changes — keeps the image in-bounds after a
  // zoom-out (which would otherwise leave empty space above/left).
  useEffect(() => {
    setPan(p => clamp(p.x, p.y));
  }, [zoom, wide, clamp]);

  const onMouseDown = (e) => {
    if (!natural.w) return;
    dragState.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
    e.preventDefault();
  };
  const onMouseMove = (e) => {
    if (!dragState.current) return;
    const ds = dragState.current;
    const nx = ds.panX + (e.clientX - ds.startX);
    const ny = ds.panY + (e.clientY - ds.startY);
    setPan(clamp(nx, ny));
  };
  const onMouseUpOrLeave = () => { dragState.current = null; };

  const changeZoom = (delta) => setZoom(z => {
    const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, +(z + delta).toFixed(2)));
    return next;
  });
  const resetView = () => { setZoom(1); setWide(false); setPan({ x: 0, y: 0 }); };

  // ─── Shared body (image + controls) ──────────────────────────
  const body = (
    <div className="flex flex-col h-full bg-[#f3efe6]/30">
      {/* Header bar */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b bg-white">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-semibold">{title || 'Receipt'}</p>
          <p className="text-sm text-[#1f2933] truncate">{receipt?.file_name || 'receipt'}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            variant="ghost" size="icon" className="h-8 w-8" onClick={() => changeZoom(-ZOOM_STEP)}
            disabled={zoom <= ZOOM_MIN + 0.001} title="Zoom out"
            data-testid="receipt-viewer-zoom-out"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="text-[11px] tabular-nums text-[#5b6773] w-10 text-center">{Math.round(zoom * 100)}%</span>
          <Button
            variant="ghost" size="icon" className="h-8 w-8" onClick={() => changeZoom(ZOOM_STEP)}
            disabled={zoom >= ZOOM_MAX - 0.001} title="Zoom in"
            data-testid="receipt-viewer-zoom-in"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost" size="icon" className="h-8 w-8" onClick={resetView}
            title="Reset" data-testid="receipt-viewer-reset"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
          {mode === 'panel' && (
            <Button
              variant="ghost" size="icon" className="h-8 w-8"
              onClick={() => setWide(w => !w)}
              title={wide ? 'Shrink panel' : 'Expand panel'}
              data-testid="receipt-viewer-toggle-wide"
            >
              {wide ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          )}
          {url && (
            <a
              href={url} target="_blank" rel="noopener noreferrer"
              className="h-8 w-8 inline-flex items-center justify-center text-[#5b6773] hover:text-[#2d948c]"
              title="Open original"
              data-testid="receipt-viewer-open-original"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
          {mode === 'panel' && onClose && (
            <Button
              variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}
              title="Close" data-testid="receipt-viewer-close"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Image viewport */}
      <div
        ref={viewportRef}
        className="flex-1 overflow-hidden relative bg-[#fbfaf7] select-none"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUpOrLeave}
        onMouseLeave={onMouseUpOrLeave}
        style={{ cursor: natural.w ? (dragState.current ? 'grabbing' : 'grab') : 'default' }}
        data-testid="receipt-viewer-viewport"
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-[#5b6773]">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Rendering PDF…
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-destructive px-4 text-center">
            {error}
          </div>
        )}
        {src && !loading && !error && (
          <img
            ref={imgRef}
            src={src}
            alt={receipt?.file_name || 'Receipt'}
            onLoad={(e) => {
              setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
            }}
            draggable={false}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: 'top left',
              willChange: 'transform',
              maxWidth: 'none',
            }}
          />
        )}
      </div>

      {extras && (
        <div className="border-t bg-white p-3 max-h-[40vh] overflow-y-auto">
          {extras}
        </div>
      )}
    </div>
  );

  if (mode === 'embedded') {
    return (
      <div className="rounded-lg border overflow-hidden" style={{ height: 'calc(100vh - 260px)', minHeight: 420 }} data-testid="receipt-viewer-embedded">
        {body}
      </div>
    );
  }

  // Panel mode: fixed right side, semi-backdrop, slide-in
  if (!open || !receipt) return null;
  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-[#1c2a24]/20 backdrop-blur-[2px]"
        onClick={onClose}
        data-testid="receipt-viewer-backdrop"
      />
      <aside
        className="fixed top-0 right-0 z-50 h-full bg-white shadow-[-12px_0_40px_rgba(16,24,40,0.15)] border-l transition-[width] duration-300"
        style={{ width: wide ? WIDE_PANEL_WIDTH : DEFAULT_PANEL_WIDTH }}
        data-testid="receipt-viewer-panel"
      >
        {body}
      </aside>
    </>
  );
}
